import React, { use, useEffect } from "react";
import Select from "react-select";
import SearchHelper from "@/utils/SearchHelper";

interface LocationInputProps {
  value?: string;
  className?: string;
  region?: number;
  onChange?: (value: any) => void;
  placeholder?: string;
}

const LocationInput: React.FC<LocationInputProps> = ({
  value,
  className,
  region,
  onChange,
  placeholder,
}) => {
  const [flatLocations, setFlatLocations] = React.useState([]);
  const [isLoadingLocation, setIsLoadingLocation] = React.useState(false);
  const [timerId, setTimerId] = React.useState<NodeJS.Timeout | null>(null);

  const [location, setLocation] = React.useState(null);

  useEffect(() => {
    if (!region) {
      (async () => {
        setIsLoadingLocation(true);
        let locations = (await SearchHelper.getAllRegions())?.map(
          (location) => {
            return {
              value: location.uuid,
              label: location.full_name,
            };
          }
        );
        setFlatLocations(locations);
        setIsLoadingLocation(false);
        await setSelectedLocation(locations);
      })();
    }
  }, []);

  useEffect(() => {
    setInitialLocationsByRegion();
  }, [region]);

  useEffect(() => {
    if (location) {
      onChange(location);
    }
  }, [location]);

  async function setSelectedLocation(locationsList: any[]) {
    let currentLocation = locationsList.find(
      (location) => location.value == value
    );
    if (!currentLocation) {
      if (value) {
        currentLocation = await SearchHelper.getLocationByUuid(value);
        if (currentLocation) {
          let _currentLocation = {
            value: currentLocation.uuid,
            label: currentLocation.full_name,
          };
          locationsList.push(_currentLocation);
          setFlatLocations(locationsList);
          setLocation(_currentLocation);
        }
      }
    } else {
      setLocation(currentLocation);
    }
  }

  async function onLocationInputChange(value: string): Promise<void> {
    if (value.length < 1) {
      return;
    }

    setIsLoadingLocation(true);

    if (timerId) {
      clearTimeout(timerId);
    }

    let _timerId = setTimeout(async () => {
      setIsLoadingLocation(true);
      try {
        let locations = (
          !region
            ? await SearchHelper.getLocationByKeyword(value)
            : await SearchHelper.getLocationByKeywordAndRegion(region, value)
        )?.map((location) => {
          return {
            value: location.uuid,
            label: location.full_name,
          };
        });

        setIsLoadingLocation(false);
        setFlatLocations(locations);
      } catch (e) {
        console.error(e);
      }
    }, 1000);

    setTimerId(_timerId);
  }

  const setInitialLocationsByRegion = async () => {
    if (region) {
      setIsLoadingLocation(true);
      let locations = (
        await SearchHelper.getLocationByKeywordAndRegion(region, "")
      )?.map((location) => {
        return {
          value: location.uuid,
          label: location.full_name,
        };
      });
      setFlatLocations(locations);
      setIsLoadingLocation(false);
      setSelectedLocation(locations);
    }
  };

  return (
    <Select
      className={className}
      options={flatLocations}
      name="location"
      isClearable={true}
      placeholder={placeholder || "Select Location"}
      isLoading={isLoadingLocation}
      onInputChange={(e) => {
        onLocationInputChange(e);
      }}
      noOptionsMessage={() => {
        return isLoadingLocation ? "Searching" : "Not found";
      }}
      value={location}
      onChange={(v: any) => {
        setLocation(v);
      }}
    />
  );
};

export default LocationInput;
